import type {
  ContentItemLinkDetails,
  ContentItemPreview,
  ContentItemsFilter,
} from '@metapic-apps/shared'
import { type ComputedRef, type Ref, ref, watch } from 'vue'

import type { AdvertiserType } from '@/lib'
import { handleContentItemFilter } from '@/lib/adapters/content-items.adapter'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { socialMediaClient } from '@/lib/services/social-media.service'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { useErrorStore } from '@/stores/error.stores'

type UseGetContentItemsReturn = {
  data: Ref<ContentItemPreview[]>
  totalPages: Ref<number>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
}

const { listContentItems, getLinkDetails } = socialMediaClient

export const useGetContentItems = (
  filterRef: Ref<ContentItemsFilter>,
  advertiser: ComputedRef<AdvertiserType | undefined>,
): UseGetContentItemsReturn => {
  const data = ref<ContentItemPreview[]>([])
  const totalPages = ref<number>(0)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchContentItems = async (filter: ContentItemsFilter, advertiserId: number) => {
    try {
      data.value = []
      isLoading.value = true
      error.value = null

      const result = await listContentItems(
        handleContentItemFilter({
          ...filter,
          status: 'approved',
          limit: 20,
          advertiser: { id: advertiserId, label: '' },
        }),
      )
      data.value = result.items
      totalPages.value = result.meta.totalPages
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watch(
    [filterRef, () => advertiser?.value],
    async () => {
      const advertiserId = advertiser.value?.id
      if (advertiserId) {
        await fetchContentItems(filterRef.value, advertiserId)
      }
    },
    { immediate: true, deep: true },
  )

  return {
    data,
    totalPages,
    isLoading,
    error,
  }
}

export const useGetContentItemDetails = (idRef: Ref<string | null>) => {
  const data = ref<ContentItemLinkDetails | null>(null)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()
  const advertiserStore = useAdvertiserStore()

  const fetchDetails = async (id: string) => {
    try {
      isLoading.value = true
      error.value = null
      const advertiserId = advertiserStore.advertiser?.id
      const result = await getLinkDetails(id, { as_advertiser_id: advertiserId })
      data.value = result
    } catch (e: unknown) {
      const parsedError = e as Error
      errorStore.setError(handleErrorMessage(e))
      error.value = parsedError
    } finally {
      isLoading.value = false
    }
  }

  watch(
    idRef,
    (id) => {
      if (id) {
        void fetchDetails(id)
      }
    },
    { immediate: true },
  )

  return { data, isLoading, error }
}
